import { useEffect, useState } from 'react';

/**
 * Subscribes to a media query and re-renders when it starts or stops matching.
 * The shell uses two of these to pick its drawer mode and rail behaviour.
 *
 * The `addListener` fallback is not about old browsers: a stub `MediaQueryList`
 * standing in where there is no `window` typically implements only the
 * deprecated pair. The defence is cheap to keep, and it also covers jsdom
 * under test, where `matchMedia` may be missing entirely.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);

    // Re-sync now: the query may have changed since the initial state ran.
    onChange();

    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }

    if (typeof list.addListener === 'function') {
      list.addListener(onChange);
      return () => list.removeListener(onChange);
    }

    return undefined;
  }, [query]);

  return matches;
}

/*
 * The shell's two breakpoints, kept in step with --bp-lg / --bp-sm in
 * src/styles/_responsive.scss.
 *
 * `COMPACT` is the one the shell reflows on: below it the left nav and the
 * assistant stop being docked columns and become overlays. It is the
 * tablet-landscape breakpoint rather than the 600px this used to use, because
 * `side` mode takes its width out of the content permanently - at 768px the
 * docked rail was spending a third of the screen on navigation and every grid
 * underneath it scrolled sideways to compensate.
 *
 * `HANDSET` is narrower and only decides degree: on a phone the assistant is
 * the whole screen rather than a 438px panel over it.
 */
export const COMPACT_QUERY = '(max-width: 1024px)';
export const HANDSET_QUERY = '(max-width: 640px)';

export const useIsCompact = () => useMediaQuery(COMPACT_QUERY);
export const useIsHandset = () => useMediaQuery(HANDSET_QUERY);
